/**
 * Search Insights Component
 * Displays search analytics and patterns for the dashboard
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useSearchTracking } from '../hooks/useSearchTracking';
import { SearchAnalytics } from '../services/searchAnalytics';
import { useTranslation } from 'react-i18next';
import { 
  Search,
  TrendingUp,
  Clock,
  Target,
  Calendar,
  BarChart3,
  Zap,
  CheckCircle2,
  Activity,
  Lightbulb,
  Plus,
  Brain
} from 'lucide-react';

interface SearchInsightsProps {
  className?: string;
  showDetailedView?: boolean;
}

export const SearchInsights: React.FC<SearchInsightsProps> = ({ 
  className,
  showDetailedView = false 
}) => {
  const { t } = useTranslation();
  const { getSearchAnalytics } = useSearchTracking();
  const [analytics, setAnalytics] = useState<SearchAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [getSearchAnalytics]);

  const loadAnalytics = async () => {
    try {
      setIsLoading(true);
      const data = await getSearchAnalytics();
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load search analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Search className="w-5 h-5 text-primary" />
            <span>{t('searchInsights.title', { ns: 'dashboard' })}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!analytics || analytics.totalSearches === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Search className="w-5 h-5 text-primary" />
            <span>{t('searchInsights.title', { ns: 'dashboard' })}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-50 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Brain className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t('searchInsights.getStarted.title', { ns: 'dashboard' })}
            </h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
              {t('searchInsights.getStarted.description', { ns: 'dashboard' })}
            </p>
            
            {/* Quick Start Actions */}
            <div className="space-y-2">
              <Button 
                size="sm" 
                className="w-full justify-start"
                variant="outline"
                onClick={() => window.location.href = '/workspace?action=search'}
              >
                <Search className="w-4 h-4 mr-2" />
                {t('searchInsights.getStarted.trySearch', { ns: 'dashboard' })}
              </Button>
              <Button 
                size="sm" 
                className="w-full justify-start"
                variant="outline"
                onClick={() => window.location.href = '/workspace'}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('searchInsights.getStarted.createMemory', { ns: 'dashboard' })}
              </Button>
            </div>

            {/* Search Tips */}
            <div className="mt-6 pt-4 border-t border-muted">
              <p className="text-xs text-muted-foreground mb-3">
                {t('searchInsights.getStarted.searchTips', { ns: 'dashboard' })}
              </p>
              <div className="space-y-2 text-xs">
                <div className="flex items-center space-x-2">
                  <Lightbulb className="w-3 h-3 text-yellow-600" />
                  <span className="text-muted-foreground">Use natural language queries</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Lightbulb className="w-3 h-3 text-yellow-600" />
                  <span className="text-muted-foreground">Search across all your memories</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Lightbulb className="w-3 h-3 text-yellow-600" />
                  <span className="text-muted-foreground">Get instant, relevant results</span>
                </div>
              </div>
            </div>

            {/* Expected Insights Preview */}
            <div className="mt-4 pt-4 border-t border-muted">
              <p className="text-xs text-muted-foreground mb-3">
                {t('searchInsights.getStarted.expectedInsights', { ns: 'dashboard' })}
              </p>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="w-3 h-3 text-green-600" />
                  <span className="text-muted-foreground">Search patterns</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-3 h-3 text-blue-600" />
                  <span className="text-muted-foreground">Peak usage times</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Target className="w-3 h-3 text-purple-600" />
                  <span className="text-muted-foreground">Success rates</span>
                </div>
                <div className="flex items-center space-x-2">
                  <BarChart3 className="w-3 h-3 text-orange-600" />
                  <span className="text-muted-foreground">Query insights</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatHour = (hour: number): string => {
    if (hour === 0) return '12 AM';
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return '12 PM';
    return `${hour - 12} PM`;
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Search className="w-5 h-5 text-primary" />
            <span>{t('searchInsights.title', { ns: 'dashboard' })}</span>
          </div>
          <Badge variant="secondary" className="text-xs">
            {t('searchInsights.detail.badgeTotalSearches', {
              ns: 'dashboard',
              count: analytics.totalSearches,
            })}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-center"
          >
            <div className="flex items-center justify-center space-x-1 mb-1">
              <Calendar className="w-4 h-4 text-blue-500" />
              <span className="text-2xl font-bold text-blue-600">
                {analytics.searchesThisWeek}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('searchInsights.detail.searchesThisWeekLabel', { ns: 'dashboard' })}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center"
          >
            <div className="flex items-center justify-center space-x-1 mb-1">
              <Activity className="w-4 h-4 text-green-500" />
              <span className="text-2xl font-bold text-green-600">
                {analytics.searchesToday}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('searchInsights.detail.searchesTodayLabel', { ns: 'dashboard' })}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center"
          >
            <div className="flex items-center justify-center space-x-1 mb-1">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <span className="text-2xl font-bold text-emerald-600">
                {analytics.successRate}%
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('searchInsights.detail.successRateLabel', { ns: 'dashboard' })}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-center"
          >
            <div className="flex items-center justify-center space-x-1 mb-1">
              <Zap className="w-4 h-4 text-orange-500" />
              <span className="text-2xl font-bold text-orange-600">
                {Math.round(analytics.averageResponseTime)}ms
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('searchInsights.detail.avgResponseLabel', { ns: 'dashboard' })}
            </p>
          </motion.div>
        </div>

        {/* Popular Queries */}
        {analytics.topQueries.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h4 className="text-sm font-medium mb-3 flex items-center space-x-2">
              <TrendingUp className="w-4 h-4 text-purple-500" />
              <span>{t('searchInsights.detail.popularQueriesTitle', { ns: 'dashboard' })}</span>
            </h4>
            <div className="space-y-2">
              {analytics.topQueries.slice(0, showDetailedView ? 10 : 5).map((query, index) => (
                <motion.div
                  key={query.query}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      "{query.query}"
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Last used: {new Date(query.lastUsed).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant="outline" className="ml-2">
                    {query.count}x
                  </Badge>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Peak Hours */}
        {analytics.searchPatterns.peakHours.length > 0 && showDetailedView && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h4 className="text-sm font-medium mb-3 flex items-center space-x-2">
              <Clock className="w-4 h-4 text-indigo-500" />
              <span>Peak Search Hours</span>
            </h4>
            <div className="grid grid-cols-3 gap-2">
              {analytics.searchPatterns.peakHours.slice(0, 6).map((hour, index) => (
                <motion.div
                  key={hour.hour}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 * index }}
                  className="text-center p-2 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg border"
                >
                  <p className="text-sm font-medium">{formatHour(hour.hour)}</p>
                  <p className="text-xs text-muted-foreground">{hour.count} searches</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Query Types */}
        {showDetailedView && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <h4 className="text-sm font-medium mb-3 flex items-center space-x-2">
              <BarChart3 className="w-4 h-4 text-green-500" />
              <span>Search Types</span>
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-lg font-bold text-blue-600">
                  {analytics.searchPatterns.queryTypes.semantic}
                </div>
                <p className="text-xs text-muted-foreground">Semantic</p>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">
                  {analytics.searchPatterns.queryTypes.keyword}
                </div>
                <p className="text-xs text-muted-foreground">Keyword</p>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-purple-600">
                  {analytics.searchPatterns.queryTypes.exact}
                </div>
                <p className="text-xs text-muted-foreground">Exact Match</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Weekly Trend */}
        {analytics.weeklyTrend.length > 0 && showDetailedView && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <h4 className="text-sm font-medium mb-3 flex items-center space-x-2">
              <TrendingUp className="w-4 h-4 text-orange-500" />
              <span>Weekly Trend</span>
            </h4>
            <div className="flex items-end space-x-1 h-16">
              {analytics.weeklyTrend.map((day, index) => (
                <motion.div
                  key={day.date}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ delay: 0.1 * index }}
                  className="flex-1 bg-gradient-to-t from-orange-500 to-orange-300 rounded-t"
                  style={{ 
                    height: `${Math.max((day.count / Math.max(...analytics.weeklyTrend.map(d => d.count))) * 100, 5)}%` 
                  }}
                  title={`${day.date}: ${day.count} searches (${day.avgResponseTime}ms avg)`}
                />
              ))}
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>7 days ago</span>
              <span>Today</span>
            </div>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}; 